/**
 * Logger Utility for Zenda
 *
 * Provides conditional logging based on environment.
 * In production, suppresses verbose logs (log/info) while preserving error/warn for debugging.
 */

/**
 * Logger interface for consistent logging across the application
 */
interface Logger {
  /** Verbose debug info (cache timings, etc). Hidden by default, visible at "Verbose" level in DevTools. */
  debug: (...args: unknown[]) => void;
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

const isProduction = import.meta.env.PROD;

/**
 * Create a logger instance that conditionally logs based on environment
 *
 * In development:
 * - debug() uses console.debug (hidden unless "Verbose" is enabled in DevTools)
 * - log(), info(), warn(), error() are active
 *
 * In production:
 * - log(), info(), and debug() are suppressed (no-ops)
 * - warn() and error() remain active for critical debugging
 */
function createLogger(): Logger {
  if (isProduction) {
    return {
      debug: () => {},
      log: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    };
  }

  return {
    debug: console.debug.bind(console),
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };
}

/**
 * Global logger instance
 * Use this throughout the application instead of console.log/info
 *
 * @example
 * import { logger } from '@/utils/logger';
 *
 * logger.log('[Cache] Loading tasks...'); // Suppressed in production
 * logger.error('[Cache] Failed to load:', error); // Always visible
 */
export const logger = createLogger();
